import { useEffect, useRef, useState } from 'react';
import {
  CharacterPromptBuilder,
  type ImageContext,
} from './CharacterPromptBuilder';
import { CHARACTER_SHEET_LAYOUT, characterSheetPrompt, reusableCharacterLook } from './characterSheet';
import { mediaFailureMessage } from './mediaErrors';
import { CharacterReferencePanel, ConfirmReferenceButton } from './CharacterReferences';
import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Download } from 'lucide-react';
import { api, type Page, type Schema } from '../api/client';
import { useStudio } from './App';
import { Dialog, Field, Spinner, useApi } from './shared';
import { useTasks, type StudioTask } from './production';

const statuses: Record<string, string> = {
  queued: '等待中',
  running: '生成中',
  completed: '已保存',
  failed: '失败',
  outcome_unknown: '结果待核对',
  cancelled: '已取消',
};

function imageTasksPath(
  base: string,
  seriesId: string,
  branchId: string | undefined,
  characterId: string,
) {
  return `${base}/studio-tasks?seriesId=${seriesId}&branchId=${branchId}&characterId=${characterId}`;
}

function CharacterImageHistory({
  character,
  tasks,
  selectedId = '',
}: {
  character: Schema<'Character'>;
  tasks: StudioTask[];
  selectedId?: string;
}) {
  const [chosenId, setChosenId] = useState('');
  const chosen =
    tasks.find((t) => t.id === (chosenId || selectedId)) ??
    tasks.find((t) => t.state === 'completed' && t.result.asset) ??
    tasks[0];
  const active = tasks.find((t) => ['queued', 'running'].includes(t.state));
  const asset = chosen?.state === 'completed' ? chosen.result.asset : undefined;
  const latestFailure = tasks[0]?.errorMessage && tasks[0].id !== chosen?.id ? tasks[0] : undefined;
  return (
    <div className="character-image-history">
      {active && (
        <p className="character-image-status" role="status">
          <Spinner />
          {active.state === 'queued' ? '图片已排队' : `图片生成中 ${active.progress}%`}
          ，关闭页面后仍会继续，完成后自动保存在这里。
        </p>
      )}
      {tasks.length > 0 && (
        <Field label="已保存的图片与生成记录">
          <select value={chosen?.id ?? ''} onChange={(e) => setChosenId(e.target.value)}>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {new Date(t.createdAt).toLocaleString()} · {statuses[t.state] ?? t.state}
              </option>
            ))}
          </select>
        </Field>
      )}
      {chosen?.errorMessage && (
        <p className="form-error" role="alert">
          {mediaFailureMessage(chosen)}
        </p>
      )}
      {latestFailure && (
        <p className="form-error" role="alert">
          最近一次生成失败：{mediaFailureMessage(latestFailure)}
        </p>
      )}
      {asset && (
        <figure className="character-image-result">
          <a
            href={asset.url}
            target="_blank"
            rel="noreferrer"
            aria-label={`查看${character.displayName}的图片原图`}
          >
            <img src={asset.url} alt={`${character.displayName}的角色图片候选`} />
          </a>
          <figcaption>
            <span>已保存 · 点击图片查看原图</span>
            <a
              className="button secondary"
              href={asset.url}
              download={`${character.displayName}.png`}
            >
              <Download size={15} />
              下载图片
            </a>
          </figcaption>
          <ConfirmReferenceButton characterId={character.id} asset={asset} />
        </figure>
      )}
      {tasks.length === 0 && (
        <p className="quiet">还没有生成角色图片。生成后会自动保存，随时可回来查看。</p>
      )}
    </div>
  );
}

export function CharacterImagesPanel({
  character,
  currentDraft,
  disabled,
}: {
  character: Schema<'Character'>;
  currentDraft: Record<string, unknown>;
  disabled?: boolean;
}) {
  const { base, project, branch } = useStudio();
  const tasks = useTasks(imageTasksPath(base, project?.id ?? '', branch?.id, character.id));
  const matching =
    tasks.data?.items.filter((t) => t.kind === 'image' && t.characterId === character.id) ?? [];
  const active = matching.some((t) => ['queued', 'running'].includes(t.state));
  const [prompt, setPrompt] = useState<string | null>(null);
  return (
    <section className="character-images-panel" aria-label="角色图片">
      <div className="character-images-heading">
        <strong>角色图片</strong>
        <button
          type="button"
          className="button secondary"
          disabled={disabled}
          onClick={() => setPrompt('')}
        >
          <ImagePlus size={16} />
          {active ? '查看生成进度' : matching.length ? '生成新图片' : '生成角色图片'}
        </button>
      </div>
      {tasks.isPending ? (
        <p role="status">正在读取图片记录…</p>
      ) : tasks.error ? (
        <p className="form-error" role="alert">
          {tasks.error.message}
        </p>
      ) : (
        <CharacterImageHistory key={character.id} character={character} tasks={matching} />
      )}
      <CharacterReferencePanel characterId={character.id} />
      {prompt !== null && (
        <CharacterImagesDialog
          character={character}
          initialPrompt={prompt}
          currentDraft={currentDraft}
          close={() => setPrompt(null)}
        />
      )}
    </section>
  );
}

function CharacterImagesDialog({
  character,
  initialPrompt,
  currentDraft,
  close,
}: {
  character: Schema<'Character'>;
  initialPrompt: string;
  currentDraft: Record<string, unknown>;
  close: () => void;
}) {
  const { base, project, branch } = useStudio();
  const cache = useQueryClient();
  const models = useApi<Page<Schema<'ModelSummary'>>>(`${base}/models`);
  const policy = useApi<Schema<'ModelPolicy'>>(`${base}/series/${project?.id}/model-policy`);
  const settings = useApi<{ callsEnabled: boolean }>(`${base}/model-settings`);
  const taskPath = imageTasksPath(base, project?.id ?? '', branch?.id, character.id);
  const tasks = useTasks(taskPath);
  const [modelId, setModelId] = useState('');
  const [prompt, setPrompt] = useState(initialPrompt);
  const touched = useRef(false);
  const initialized = useRef(false);
  const context = useApi<ImageContext>(
    `${base}/series/${project?.id}/characters/${character.id}/image-context?branchId=${branch?.id}`,
    !!project && !!branch,
  );
  const changePrompt = (value: string) => {
    touched.current = true;
    setPrompt(value);
  };
  useEffect(() => {
    if (initialized.current || !context.data || !tasks.data) return;
    initialized.current = true;
    if (!touched.current) {
      const saved = tasks.data.items.find(
        (t) =>
          t.characterId === character.id &&
          t.state === 'completed' &&
          t.result.characterImagePrompt?.version === 2,
      )?.result.characterImagePrompt;
      setPrompt(
        reusableCharacterLook(saved),
      );
    }
  }, [context.data, tasks.data, character.id, character.displayName, currentDraft]);
  const [chosenId, setChosenId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const options = models.data?.items.filter((m) => /image/i.test(m.upstreamModelId)) ?? [];
  const preferred = policy.data?.bindings.image?.primary.catalogModelId;
  const selectedId =
    modelId ||
    preferred ||
    options.find((m) => m.upstreamModelId === 'gpt-image-2.5')?.catalogModelId ||
    options[0]?.catalogModelId ||
    '';
  const selected = options.find((m) => m.catalogModelId === selectedId);
  const matching =
    tasks.data?.items.filter((t) => t.kind === 'image' && t.characterId === character.id) ?? [];
  const active = matching.find((t) => ['queued', 'running'].includes(t.state));
  async function generate() {
    if (!project || !branch || !selected) return;
    setBusy(true);
    setError('');
    try {
      const task = await api<StudioTask>(`${base}/studio-tasks`, 'POST', {
        kind: 'image',
        seriesId: project.id,
        branchId: branch.id,
        characterId: character.id,
        modelId: selected.catalogModelId,
        prompt: characterSheetPrompt(character.displayName, prompt),
        confirmed: true,
      });
      setChosenId(task.id);
      await cache.invalidateQueries({ queryKey: ['api', taskPath] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={`${character.displayName} · 生成角色四视图`}
      subtitle="一张图片展示同一套造型的四个视角，生成后自动保存，可预览和下载。"
      close={close}
    >
      <div className="dialog-body character-image-generator">
        {context.isPending ? (
          <p>
            <Spinner />
            正在读取原文与人设…
          </p>
        ) : context.error ? (
          <p role="alert" className="form-error">
            {context.error.message}
          </p>
        ) : (
          context.data && (
            <CharacterPromptBuilder
              characterId={character.id}
              draft={currentDraft}
              context={context.data}
              taskPath={taskPath}
              disabled={busy || !!active || !settings.data?.callsEnabled}
              onAdopt={changePrompt}
            />
          )
        )}
        <strong>2. 确认静态造型并生成四视图</strong>
        <p className="quiet">固定布局：左上正面、右上左侧、左下背面、右下右侧。四格均为全身，服装、发型、年龄一致。</p>
        <Field label="生图模型">
          <select
            value={selectedId}
            disabled={busy || !!active}
            onChange={(e) => setModelId(e.target.value)}
          >
            {!selected && (
              <option value={selectedId}>
                {selectedId ? '已绑定的生图模型不可用，请重新选择' : '暂无生图模型'}
              </option>
            )}
            {options.map((m) => (
              <option key={m.catalogModelId} value={m.catalogModelId}>
                {m.displayName}
                {m.selectable ? '' : ' · 尚未验证'}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="本套静态造型（可编辑）"
          hint="先采用上方新候选，或手动填写具体外观。只写这一套服装和外观，其他年龄、换装阶段另建候选。"
        >
          <textarea
            rows={10}
            maxLength={14000}
            placeholder="面容、发型、体型、唯一一套完整服装、固定配饰、绘制风格。不会自动拼入人物经历或旧版多阶段描述。"
            value={prompt}
            onChange={(e) => changePrompt(e.target.value)}
            disabled={busy || !!active}
          />
        </Field>
        <details>
          <summary>实际发送给图片模型的完整提示词</summary>
          <textarea readOnly rows={10} aria-label="完整四视图提示词" value={prompt.trim() ? characterSheetPrompt(character.displayName, prompt) : CHARACTER_SHEET_LAYOUT} />
        </details>
        <button
          type="button"
          className="button primary"
          disabled={
            busy ||
            !!active ||
            !selected ||
            !prompt.trim() ||
            !settings.data?.callsEnabled ||
            !project ||
            !branch ||
            policy.isPending ||
            !!policy.error ||
            tasks.isPending ||
            !!tasks.error
          }
          onClick={() => void generate()}
        >
          {busy || active ? <Spinner /> : <ImagePlus size={16} />}
          {active ? '图片生成中，可关闭后回来查看' : '生成四视图候选'}
        </button>
        {(error || models.error || policy.error || tasks.error || settings.error) && (
          <p className="form-error" role="alert">
            {error || (models.error ?? policy.error ?? tasks.error ?? settings.error)?.message}
          </p>
        )}
        <CharacterImageHistory
          key={chosenId}
          character={character}
          tasks={matching}
          selectedId={chosenId}
        />
        <p className="quiet">
          四个视角合在一张图中，使用时占一张参考图名额。确认四格外观一致后，可在预览下方确认视频参考图；后续支持参考图的关键帧和视频任务会使用已确认的角色图片。
        </p>
      </div>
    </Dialog>
  );
}
